import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Briefcase, Plus, Sparkles, DollarSign, Clock, Tag, X, Loader2, MessageSquare, Send, ShieldCheck, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { errorMessage } from "@/lib/errors";
import ProfileHub from "./ProfileHub";
import BriefHelperModal from "./BriefHelperModal";
import AriaDeliveryProgress from "./AriaDeliveryProgress";
import DatasetUploadModal from "./DatasetUploadModal";

/**
 * MarketplaceV2 — the user-posts-jobs + freelancers-bid + Stripe-escrow
 * marketplace. Sits on /cartblancja under the SERVICES grid.
 *
 * Tabs:
 *   • Browse Jobs — public feed, anyone can bid
 *   • My Jobs    — jobs I posted (auth)
 *   • My Bids    — bids I've placed (auth)
 *   • ARIA Agents — pre-built AI freelancers (B3 stub)
 *
 * Designed to feel premium: gold accents, large CTA, generous spacing.
 */
const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "web-development", label: "Web Dev" },
  { id: "mobile-app", label: "Mobile" },
  { id: "ai-ml", label: "AI / ML" },
  { id: "design", label: "Design" },
  { id: "writing", label: "Writing" },
  { id: "video", label: "Video" },
  { id: "marketing", label: "Marketing" },
  { id: "data", label: "Data" },
  { id: "blockchain", label: "Blockchain" },
  { id: "iot", label: "IoT" },
  { id: "metaverse", label: "Metaverse" },
  { id: "ar-vr", label: "AR / VR" },
  { id: "robotics", label: "Robotics" },
  { id: "agentic-ai", label: "Agentic AI" },
  { id: "consulting", label: "Consulting" },
  { id: "other", label: "Other" },
];

const TABS = [
  { id: "browse", label: "Browse Jobs" },
  { id: "my-jobs", label: "My Jobs" },
  { id: "my-bids", label: "My Bids" },
  { id: "agents", label: "ARIA Agents" },
  { id: "profile", label: "Profile Hub" },
];

const fmt = (n) => new Intl.NumberFormat("en-JM").format(Math.round(n || 0));

export default function MarketplaceV2() {
  const [tab, setTab] = useState("browse");
  const [category, setCategory] = useState("all");
  const [jobs, setJobs] = useState([]);
  const [myJobs, setMyJobs] = useState([]);
  const [myBids, setMyBids] = useState([]);
  const [agents, setAgents] = useState([]);
  const [postOpen, setPostOpen] = useState(false);
  const [openJob, setOpenJob] = useState(null);
  const [loading, setLoading] = useState(false);

  const isAuth = typeof window !== "undefined" && Boolean(localStorage.getItem("auth_token"));

  const loadJobs = async () => {
    setLoading(true);
    try {
      const params = category === "all" ? {} : { category };
      const res = await api.get("/marketplace/jobs", { params });
      setJobs(res.data?.items || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  const loadMyJobs = async () => {
    if (!isAuth) return;
    try {
      const res = await api.get("/marketplace/jobs", { params: { status: "", posted_by_me: true } });
      setMyJobs(res.data?.items || []);
    } catch { /* ignore */ }
  };

  const loadAgents = async () => {
    try {
      const res = await api.get("/marketplace/agents");
      setAgents(res.data?.items || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadJobs(); }, [category]); // eslint-disable-line
  useEffect(() => { loadAgents(); }, []);
  useEffect(() => {
    if (tab === "my-jobs") loadMyJobs();
  }, [tab]); // eslint-disable-line

  const counts = useMemo(() => ({
    browse: jobs.length, my_jobs: myJobs.length, my_bids: myBids.length, agents: agents.length,
  }), [jobs, myJobs, myBids, agents]);

  return (
    <div data-testid="marketplace-v2" className="space-y-6">
      {/* HEADER + POST CTA */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-gold mb-1">CartBlancheJA Marketplace</p>
          <h2 className="font-heading text-3xl md:text-4xl text-white">Hire elite talent. Post any job.</h2>
          <p className="text-white/50 text-sm mt-1 max-w-2xl">Bid-based marketplace with escrow milestones, 2-way reviews, and ARIA-powered AI freelancers.</p>
        </div>
        <button data-testid="post-job-btn" onClick={() => setPostOpen(true)}
          className="inline-flex items-center gap-2 px-5 py-3 bg-gold text-luxury-black text-xs font-body uppercase tracking-wider rounded hover:bg-gold/90 transition-colors">
          <Plus size={14} /> Post a Job
        </button>
      </div>

      {/* TABS */}
      <div className="flex gap-1 border-b border-white/10">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-xs font-body uppercase tracking-wider border-b-2 transition-colors ${
              tab === t.id
                ? "border-gold text-gold"
                : "border-transparent text-white/50 hover:text-white"
            }`}
          >
            {t.label} {(t.id === "browse" || t.id === "agents") && (
              <span className="ml-1 text-[10px] opacity-60">{t.id === "browse" ? counts.browse : counts.agents}</span>
            )}
          </button>
        ))}
      </div>

      {/* TAB BODY */}
      {tab === "browse" && (
        <BrowseJobs
          jobs={jobs} loading={loading} category={category} setCategory={setCategory}
          onPick={setOpenJob}
        />
      )}
      {tab === "my-jobs" && (
        <MyJobs items={myJobs} onPick={setOpenJob} isAuth={isAuth} />
      )}
      {tab === "my-bids" && (
        <MyBids isAuth={isAuth} />
      )}
      {tab === "agents" && (
        <AgentsGrid agents={agents} />
      )}
      {tab === "profile" && (
        <ProfileHub />
      )}

      {/* POST JOB MODAL */}
      {postOpen && (
        <PostJobModal
          onClose={() => setPostOpen(false)}
          onPosted={() => { setPostOpen(false); loadJobs(); loadMyJobs(); toast.success("Job posted — freelancers will start bidding"); }}
        />
      )}
      {/* JOB DETAIL */}
      {openJob && (
        <JobDetailModal job={openJob} onClose={() => setOpenJob(null)} onChanged={() => { loadJobs(); loadMyJobs(); }} />
      )}
    </div>
  );
}

// =============================================================
// SUB-COMPONENTS
// =============================================================

function BrowseJobs({ jobs, loading, category, setCategory, onPick }) {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1.5">
        {CATEGORIES.map((c) => (
          <button
            key={c.id}
            data-testid={`cat-${c.id}`}
            onClick={() => setCategory(c.id)}
            className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border transition-colors ${
              category === c.id
                ? "border-gold/60 bg-gold/15 text-gold"
                : "border-white/15 text-white/60 hover:border-gold/30 hover:text-white"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="text-center py-12 text-white/40"><Loader2 size={20} className="animate-spin inline" /></div>
      ) : jobs.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-white/10 rounded">
          <Briefcase size={28} className="mx-auto text-white/20 mb-3" />
          <p className="text-white/50 font-body text-sm">No jobs in this category yet — be the first to post one.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {jobs.map((j) => <JobCard key={j.id} job={j} onPick={onPick} />)}
        </div>
      )}
    </div>
  );
}

function JobCard({ job, onPick }) {
  // Surface "✨ ARIA agent has bid" hint on the card. We don't fetch
  // bids here (would be N+1) — instead rely on a backend hint
  // `aria_agent_bid` boolean if present. Backward-compat: hide if missing.
  return (
    <motion.button
      data-testid={`job-card-${job.id}`}
      onClick={() => onPick(job)}
      whileHover={{ y: -2 }}
      className="text-left p-4 border border-white/10 hover:border-gold/30 bg-black/30 rounded-md transition-colors"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-gold/15 text-gold border border-gold/30">
          {job.category}
        </span>
        <span className="text-[10px] text-white/50">{job.proposals_count || 0} bids</span>
      </div>
      <h3 className="font-heading text-base text-white mb-1 line-clamp-2">{job.title}</h3>
      <p className="text-xs text-white/55 line-clamp-2 mb-3">{job.description}</p>
      <div className="flex items-center justify-between text-[11px]">
        <span className="inline-flex items-center gap-1 text-gold font-display">
          <DollarSign size={11} /> {job.currency} {fmt(job.budget_min)} – {fmt(job.budget_max)}
        </span>
        {job.duration && (
          <span className="inline-flex items-center gap-1 text-white/45">
            <Clock size={10} /> {job.duration}
          </span>
        )}
      </div>
      {job.aria_agent_bid && (
        <div className="mt-2 pt-2 border-t border-gold/20 text-[10px] text-gold/80 inline-flex items-center gap-1">
          <Sparkles size={10} /> ARIA Agent has bid
        </div>
      )}
    </motion.button>
  );
}

function MyJobs({ items, onPick, isAuth }) {
  if (!isAuth) return <p className="text-white/50 text-sm">Sign in to see your posted jobs.</p>;
  if (!items.length) return <p className="text-white/50 text-sm">You haven't posted any jobs yet.</p>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {items.map((j) => <JobCard key={j.id} job={j} onPick={onPick} />)}
    </div>
  );
}

function MyBids({ isAuth }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!isAuth) return;
    // Fetch bids per job-status efficiently → we hit /marketplace/jobs?status= to
    // see jobs we've engaged with. Simple fallback: show empty until we have a
    // dedicated /me/bids endpoint (parked for now to keep the surface minimal).
    setItems([]);
  }, [isAuth]);
  if (!isAuth) return <p className="text-white/50 text-sm">Sign in to see your bids.</p>;
  return (
    <p data-testid="my-bids-empty" className="text-white/50 text-sm">
      {items.length === 0 ? "Your active bids will appear here. Open any job and submit a bid to start." : ""}
    </p>
  );
}

function WithdrawBidButton({ bid, onWithdraw, busy }) {
  const [secsLeft, setSecsLeft] = useState(() => {
    if (!bid.withdraw_locked_until) return 0;
    const ms = new Date(bid.withdraw_locked_until).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / 1000));
  });
  useEffect(() => {
    if (secsLeft <= 0) return undefined;
    const id = setInterval(() => setSecsLeft((n) => Math.max(0, n - 1)), 1000);
    return () => clearInterval(id);
  }, [secsLeft]);
  const locked = secsLeft > 0;
  const mm = Math.floor(secsLeft / 60).toString().padStart(2, "0");
  const ss = (secsLeft % 60).toString().padStart(2, "0");
  return (
    <button
      data-testid={`withdraw-bid-${bid.id}`}
      onClick={onWithdraw}
      disabled={locked || busy}
      title={locked ? `Bid locked — try again in ${mm}:${ss}` : "Withdraw your bid"}
      className={`mt-2 inline-flex items-center gap-1 px-3 py-1.5 text-[10px] uppercase tracking-wider rounded border ${
        locked
          ? "border-white/15 text-white/35 cursor-not-allowed"
          : "border-rose-400/40 text-rose-300 hover:bg-rose-500/10"
      }`}>
      {locked ? `Locked · ${mm}:${ss}` : "Withdraw bid"}
    </button>
  );
}

function AgentsGrid({ agents }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
      {agents.map((a) => (
        <div key={a.agent_id} data-testid={`agent-${a.agent_id}`}
          className="p-4 border border-gold/30 bg-gradient-to-br from-gold/[0.06] to-transparent rounded-md">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-3xl">{a.avatar_emoji}</span>
            <div>
              <h4 className="text-white font-heading text-sm">{a.name}</h4>
              <p className="text-[10px] text-gold/70 uppercase tracking-wider">{a.rating} ★ ({a.reviews})</p>
            </div>
          </div>
          <p className="text-[11px] text-white/55 mb-2">{a.blurb}</p>
          <div className="flex flex-wrap gap-1 mb-2">
            {a.skills.slice(0, 4).map((s) => (
              <span key={s} className="text-[9px] uppercase px-1.5 py-0.5 rounded-full border border-white/15 text-white/60">{s}</span>
            ))}
          </div>
          <div className="flex items-center justify-between text-[11px] pt-2 border-t border-white/10">
            <span className="text-gold font-display">JMD {fmt(a.rate_jmd_per_task)}/task</span>
            <span className="text-white/40">JMD {fmt(a.rate_jmd_per_hour)}/hr</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// =============================================================
// MODALS
// =============================================================

function PostJobModal({ onClose, onPosted }) {
  const [busy, setBusy] = useState(false);
  const [briefHelperOpen, setBriefHelperOpen] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", category: "web-development",
    budget_min: 5000, budget_max: 15000, currency: "JMD",
    duration: "1 week", skills: "",
    milestones: [{ title: "Final delivery", amount: 5000 }],
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateMilestone = (i, k, v) => setForm((f) => ({
    ...f, milestones: f.milestones.map((m, idx) => idx === i ? { ...m, [k]: v } : m),
  }));

  const submit = async () => {
    if (!form.title || form.title.length < 5 || !form.description || form.description.length < 20) {
      toast.error("Title (5+ chars) and description (20+ chars) required");
      return;
    }
    setBusy(true);
    try {
      const skills = form.skills.split(",").map((s) => s.trim()).filter(Boolean);
      await api.post("/marketplace/jobs", {
        ...form, skills,
        budget_min: Number(form.budget_min) || 0,
        budget_max: Number(form.budget_max) || 0,
        milestones: form.milestones.filter(m => m.title).map(m => ({ ...m, amount: Number(m.amount) || 0 })),
      });
      onPosted?.();
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't post job"));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={() => !busy && onClose()}>
      <div onClick={(e) => e.stopPropagation()}
        data-testid="post-job-modal"
        className="w-full max-w-2xl bg-luxury-black border border-gold/30 rounded-lg p-6 space-y-4 my-8">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-gold">CartBlancheJA</p>
            <h3 className="font-heading text-xl text-white mt-1">Post a job</h3>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>
        <div className="space-y-3">
          <input data-testid="job-title" value={form.title} onChange={(e) => update("title", e.target.value)}
            placeholder="Job title (e.g. Build me a landing page)" maxLength={140}
            className="w-full bg-black/40 border border-white/15 rounded px-3 py-2 text-sm text-white focus:border-gold/60 focus:outline-none" />
          <textarea data-testid="job-description" value={form.description} onChange={(e) => update("description", e.target.value)}
            placeholder="Describe the work — what, how, by when. The clearer the brief, the better the bids." rows={4} maxLength={4000}
            className="w-full bg-black/40 border border-white/15 rounded px-3 py-2 text-sm text-white focus:border-gold/60 focus:outline-none" />
          <div className="grid grid-cols-2 gap-2">
            <select data-testid="job-category" value={form.category} onChange={(e) => update("category", e.target.value)}
              className="bg-black/40 border border-white/15 rounded px-3 py-2 text-sm text-white focus:border-gold/60 focus:outline-none">
              {CATEGORIES.filter((c) => c.id !== "all").map((c) => (<option key={c.id} value={c.id}>{c.label}</option>))}
            </select>
            <input value={form.duration} onChange={(e) => update("duration", e.target.value)}
              placeholder="Duration (e.g. 1 week)"
              className="bg-black/40 border border-white/15 rounded px-3 py-2 text-sm text-white focus:border-gold/60 focus:outline-none" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <input type="number" data-testid="job-budget-min" value={form.budget_min} onChange={(e) => update("budget_min", e.target.value)}
              placeholder="Min" className="bg-black/40 border border-white/15 rounded px-3 py-2 text-sm text-white focus:border-gold/60 focus:outline-none" />
            <input type="number" data-testid="job-budget-max" value={form.budget_max} onChange={(e) => update("budget_max", e.target.value)}
              placeholder="Max" className="bg-black/40 border border-white/15 rounded px-3 py-2 text-sm text-white focus:border-gold/60 focus:outline-none" />
            <select value={form.currency} onChange={(e) => update("currency", e.target.value)}
              className="bg-black/40 border border-white/15 rounded px-3 py-2 text-sm text-white focus:border-gold/60 focus:outline-none">
              <option value="JMD">JMD</option><option value="USD">USD</option><option value="GBP">GBP</option><option value="EUR">EUR</option>
            </select>
          </div>
          <input value={form.skills} onChange={(e) => update("skills", e.target.value)}
            placeholder="Skills (comma-separated, e.g. react, tailwind, stripe)"
            className="w-full bg-black/40 border border-white/15 rounded px-3 py-2 text-sm text-white focus:border-gold/60 focus:outline-none" />
          <div>
            <p className="text-[10px] uppercase tracking-wider text-gold mb-2">Milestones (escrowed)</p>
            <div className="space-y-1.5">
              {form.milestones.map((m, i) => (
                <div key={i} className="flex gap-2">
                  <input data-testid={`milestone-title-${i}`} value={m.title} onChange={(e) => updateMilestone(i, "title", e.target.value)}
                    placeholder={`Milestone ${i + 1} title`}
                    className="flex-1 bg-black/40 border border-white/15 rounded px-3 py-2 text-sm text-white" />
                  <input data-testid={`milestone-amount-${i}`} type="number" value={m.amount} onChange={(e) => updateMilestone(i, "amount", e.target.value)}
                    placeholder="Amount" className="w-28 bg-black/40 border border-white/15 rounded px-3 py-2 text-sm text-white" />
                </div>
              ))}
              <button type="button" onClick={() => setForm((f) => ({ ...f, milestones: [...f.milestones, { title: "", amount: 0 }] }))}
                className="text-[10px] text-gold/70 hover:text-gold uppercase tracking-wider">+ Add milestone</button>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button data-testid="brief-helper-open" type="button" onClick={() => setBriefHelperOpen(true)} disabled={busy || !form.description.trim()}
            className="px-4 py-3 border border-gold/40 bg-gold/10 text-gold text-xs font-body uppercase tracking-wider rounded hover:bg-gold/20 disabled:opacity-40">
            <Sparkles size={14} className="inline mr-1" /> Improve with ARIA
          </button>
          <button data-testid="post-job-submit" onClick={submit} disabled={busy}
            className="flex-1 inline-flex items-center justify-center gap-2 py-3 bg-gold text-luxury-black text-xs font-body uppercase tracking-wider rounded hover:bg-gold/90 disabled:opacity-40">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {busy ? "Posting…" : "Post job"}
          </button>
        </div>
      </div>
      {briefHelperOpen && (
        <BriefHelperModal
          brief={form.description}
          category={form.category}
          skills={form.skills.split(",").map(s => s.trim()).filter(Boolean)}
          onAccept={(newBrief) => update("description", newBrief)}
          onClose={() => setBriefHelperOpen(false)}
        />
      )}
    </div>
  );
}

function JobDetailModal({ job: initialJob, onClose, onChanged }) {
  const [job, setJob] = useState(initialJob);
  const [bids, setBids] = useState([]);
  const [bidForm, setBidForm] = useState({
    amount: initialJob.budget_min || 0,
    delivery_days: 7, proposal: "", portfolio_url: "",
    currency: initialJob.currency || "JMD",
    payment_type: "cash", equity_terms: "",
  });
  const [busy, setBusy] = useState(false);
  const [dataset, setDataset] = useState(null);
  const [datasetModalOpen, setDatasetModalOpen] = useState(false);
  const [pendingAnalystBidId, setPendingAnalystBidId] = useState(null);

  const myEmail = typeof window !== "undefined" ? (JSON.parse(localStorage.getItem("auth_user") || "{}").email || null) : null;
  const isPoster = myEmail && myEmail === job.posted_by_email;

  useEffect(() => {
    api.get(`/marketplace/jobs/${initialJob.id}/bids`).then((res) => setBids(res.data?.items || [])).catch(() => {});
    api.get(`/marketplace/jobs/${initialJob.id}`).then((res) => setJob(res.data)).catch(() => {});
  }, [initialJob.id]);

  // Poster + awarded freelancer can see the dataset metadata.
  useEffect(() => {
    if (!myEmail) return;
    if (myEmail !== job.posted_by_email && myEmail !== job.awarded_to_email) return;
    api.get(`/marketplace/jobs/${initialJob.id}/dataset`)
      .then((res) => setDataset(res.data?.dataset || null))
      .catch(() => {});
  }, [initialJob.id, myEmail, job.posted_by_email, job.awarded_to_email]);

  const submitBid = async () => {
    if (!bidForm.proposal || bidForm.proposal.length < 20) {
      toast.error("Proposal needs ≥ 20 chars");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/marketplace/jobs/${job.id}/bid`, {
        amount: Number(bidForm.amount) || 0,
        delivery_days: Number(bidForm.delivery_days) || 7,
        proposal: bidForm.proposal,
        portfolio_url: bidForm.portfolio_url || null,
        currency: bidForm.currency,
        payment_type: bidForm.payment_type,
        equity_terms: bidForm.payment_type === "cash" ? null : bidForm.equity_terms,
      });
      toast.success("Bid submitted · locked for 10 minutes");
      setBidForm({ ...bidForm, proposal: "" });
      const refreshed = await api.get(`/marketplace/jobs/${job.id}/bids`);
      setBids(refreshed.data?.items || []);
      onChanged?.();
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't place bid"));
    } finally { setBusy(false); }
  };

  const withdrawBid = async (bidId) => {
    if (!confirm("Withdraw this bid?")) return;
    setBusy(true);
    try {
      await api.post(`/marketplace/bids/${bidId}/withdraw`);
      toast.success("Bid withdrawn");
      const refreshed = await api.get(`/marketplace/jobs/${job.id}/bids`);
      setBids(refreshed.data?.items || []);
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't withdraw"));
    } finally { setBusy(false); }
  };

  const performAccept = async (bidId) => {
    setBusy(true);
    try {
      const res = await api.post(`/marketplace/bids/${bidId}/accept`);
      toast.success(`Bid accepted — ${res.data.milestones?.length || 0} milestones funded${res.data.milestones?.[0]?.stripe_mocked ? " (test mode)" : ""}`);
      onChanged?.();
      onClose();
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't accept bid"));
    } finally { setBusy(false); }
  };

  const acceptBid = async (bidId) => {
    const target = bids.find((b) => b.id === bidId);
    const isAnalyst = target?.freelancer_type === "aria_agent" && target?.agent_id === "aria-analyst";
    // ARIA-Analyst needs a dataset before it can deliver. If the
    // client hasn't attached one yet, surface the upload modal first
    // — we accept the bid right after the upload succeeds.
    if (isAnalyst && !dataset) {
      setPendingAnalystBidId(bidId);
      setDatasetModalOpen(true);
      return;
    }
    if (!confirm(`Accept this bid? Funds will be escrowed via Stripe (milestone-based).`)) return;
    await performAccept(bidId);
  };

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={() => !busy && onClose()}>
      <div onClick={(e) => e.stopPropagation()}
        data-testid="job-detail-modal"
        className="w-full max-w-3xl bg-luxury-black border border-gold/30 rounded-lg p-6 space-y-4 my-8">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-gold">{job.category}</p>
            <h3 className="font-heading text-xl text-white mt-1">{job.title}</h3>
            <p className="text-[11px] text-white/50 mt-1">Posted by {job.posted_by_name || "—"}</p>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>

        <div className="grid grid-cols-2 gap-3 text-xs">
          <div className="p-2 bg-black/40 border border-white/10 rounded">
            <p className="text-[9px] uppercase tracking-wider text-white/40">Budget</p>
            <p className="text-gold font-display text-base">{job.currency} {fmt(job.budget_min)} – {fmt(job.budget_max)}</p>
          </div>
          <div className="p-2 bg-black/40 border border-white/10 rounded">
            <p className="text-[9px] uppercase tracking-wider text-white/40">Status</p>
            <p className="text-white font-display text-base capitalize">{job.status}</p>
          </div>
        </div>

        <div>
          <p className="text-[10px] uppercase tracking-wider text-gold mb-1">Brief</p>
          <p className="text-sm text-white/75 whitespace-pre-line">{job.description}</p>
        </div>

        {Boolean(job.skills?.length) && (
          <div className="flex flex-wrap gap-1.5">
            {job.skills.map((s) => (
              <span key={s} className="text-[10px] uppercase px-2 py-0.5 rounded-full border border-white/15 text-white/60">
                <Tag size={9} className="inline mr-1" /> {s}
              </span>
            ))}
          </div>
        )}

        {/* Dataset section (poster + awarded freelancer) */}
        {(isPoster || (myEmail && myEmail === job.awarded_to_email)) && (
          <div data-testid="dataset-section" className="border border-white/10 bg-black/30 rounded p-3">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] uppercase tracking-wider text-gold inline-flex items-center gap-1.5">
                <FileSpreadsheet size={11} /> Dataset
              </p>
              {isPoster && (
                <button
                  data-testid="open-dataset-modal-btn"
                  onClick={() => setDatasetModalOpen(true)}
                  className="text-[10px] uppercase tracking-wider text-gold hover:text-white">
                  {dataset ? "Replace" : "Upload CSV / XLSX"}
                </button>
              )}
            </div>
            {dataset ? (
              <div className="flex items-center gap-3 text-[11px] text-white/65">
                <span className="font-mono truncate max-w-[200px]" data-testid="dataset-summary-name">{dataset.name}</span>
                <span>·</span>
                <span>{dataset.rows?.toLocaleString()} rows × {dataset.col_count} cols</span>
                <span>·</span>
                <span>{Math.round((dataset.size_bytes || 0) / 1024)} KB</span>
              </div>
            ) : (
              <p className="text-[11px] text-white/45">
                {isPoster
                  ? "Attach a CSV or XLSX to unlock ARIA-Analyst auto-delivery."
                  : "No dataset attached yet."}
              </p>
            )}
          </div>
        )}

        {/* ARIA agent auto-delivery progress (only shows when a delivery is running/done) */}
        <AriaDeliveryProgress
          jobId={job.id}
          visible={true}
          onPendingDataset={() => { if (isPoster && !dataset) setDatasetModalOpen(true); }}
        />

        {/* Bids */}
        <div>
          <p className="text-[10px] uppercase tracking-wider text-gold mb-2">Bids ({bids.length})</p>
          {bids.length === 0 ? (
            <p className="text-white/40 text-xs">No bids yet — ARIA agents and freelancers will start bidding shortly.</p>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {bids.map((b) => {
                const isAria = b.freelancer_type === "aria_agent";
                return (
                  <div key={b.id} data-testid={`bid-${b.id}`}
                    className={`p-3 border rounded ${
                      b.status === "accepted"
                        ? "border-emerald-400/40 bg-emerald-500/10"
                        : isAria
                          ? "border-gold/40 bg-gradient-to-br from-gold/[0.08] to-transparent"
                          : "border-white/10 bg-black/30"
                    }`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5">
                        {isAria && (
                          <span data-testid={`bid-aria-badge-${b.id}`} className="text-base">
                            {b.agent_avatar_emoji || "✨"}
                          </span>
                        )}
                        <p className={`text-xs ${isAria ? "text-gold font-display" : "text-white"}`}>
                          {b.freelancer_name}
                        </p>
                        {isAria && (
                          <span className="text-[8px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-gold/15 text-gold border border-gold/40">
                            ARIA Agent
                          </span>
                        )}
                      </div>
                      <span className="text-[10px] uppercase tracking-wider text-white/40">{b.status}</span>
                    </div>
                    <div className="flex items-center gap-3 text-[11px] text-white/60 mb-1">
                      <span className="text-gold font-display">{b.currency} {fmt(b.amount)}</span>
                      <span>· {b.delivery_days}d</span>
                      {b.payment_type && b.payment_type !== "cash" && (
                        <span data-testid={`bid-pay-type-${b.id}`}
                          className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-violet-500/20 text-violet-200 border border-violet-400/40">
                          {b.payment_type === "equity" ? "Equity" : "Equity + Cash"}
                        </span>
                      )}
                      {isAria && b.agent_rating && (
                        <span className="text-gold/70">★ {b.agent_rating} ({b.agent_reviews})</span>
                      )}
                    </div>
                    <p className="text-[11px] text-white/55 line-clamp-3">{b.proposal}</p>
                    {b.equity_terms && (
                      <p data-testid={`bid-equity-${b.id}`} className="text-[10px] text-violet-200/80 mt-1 italic">
                        Equity: {b.equity_terms}
                      </p>
                    )}
                    {isPoster && b.status === "submitted" && job.status === "open" && (
                      <button data-testid={`accept-bid-${b.id}`} onClick={() => acceptBid(b.id)} disabled={busy}
                        className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 text-[10px] uppercase tracking-wider bg-emerald-500 text-luxury-black rounded hover:bg-emerald-400 disabled:opacity-40">
                        <ShieldCheck size={11} /> Accept & escrow
                      </button>
                    )}
                    {!isPoster && b.freelancer_email === myEmail && b.status === "submitted" && (
                      <WithdrawBidButton bid={b} onWithdraw={() => withdrawBid(b.id)} busy={busy} />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Place a bid (non-poster, open status only) */}
        {!isPoster && job.status === "open" && myEmail && (
          <div className="border-t border-white/10 pt-3 space-y-2">
            <p className="text-[10px] uppercase tracking-wider text-gold">Place your bid</p>
            <div className="grid grid-cols-3 gap-2">
              <input data-testid="bid-amount" type="number" value={bidForm.amount} onChange={(e) => setBidForm({ ...bidForm, amount: e.target.value })}
                placeholder="Amount" className="bg-black/40 border border-white/15 rounded px-3 py-2 text-sm text-white" />
              <input data-testid="bid-days" type="number" value={bidForm.delivery_days} onChange={(e) => setBidForm({ ...bidForm, delivery_days: e.target.value })}
                placeholder="Days" className="bg-black/40 border border-white/15 rounded px-3 py-2 text-sm text-white" />
              <select data-testid="bid-currency" value={bidForm.currency}
                onChange={(e) => setBidForm({ ...bidForm, currency: e.target.value })}
                className="bg-black/40 border border-white/15 rounded px-3 py-2 text-sm text-white">
                <option value="JMD">JMD</option>
                <option value="USD">USD</option>
                <option value="GBP">GBP</option>
                <option value="EUR">EUR</option>
                <option value="BTC">BTC</option>
                <option value="ETH">ETH</option>
                <option value="USDT">USDT</option>
                <option value="USDC">USDC</option>
              </select>
            </div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider">
              <span className="text-white/45">Pay:</span>
              {["cash", "equity", "hybrid"].map((t) => (
                <button key={t} type="button"
                  data-testid={`bid-paytype-${t}`}
                  onClick={() => setBidForm({ ...bidForm, payment_type: t })}
                  className={`px-2 py-0.5 rounded-full border transition-colors ${
                    bidForm.payment_type === t
                      ? "border-gold/60 bg-gold/15 text-gold"
                      : "border-white/15 text-white/55 hover:text-white"
                  }`}>
                  {t === "hybrid" ? "Equity + Cash" : t}
                </button>
              ))}
            </div>
            {bidForm.payment_type !== "cash" && (
              <input data-testid="bid-equity-terms" value={bidForm.equity_terms}
                maxLength={200}
                onChange={(e) => setBidForm({ ...bidForm, equity_terms: e.target.value })}
                placeholder="Equity ask — e.g. '5% common stock vested over 2 years'"
                className="w-full bg-black/40 border border-violet-400/30 rounded px-3 py-2 text-sm text-violet-100 placeholder-violet-300/40" />
            )}
            <textarea data-testid="bid-proposal" value={bidForm.proposal} onChange={(e) => setBidForm({ ...bidForm, proposal: e.target.value })}
              placeholder="Why you? (20+ chars)" rows={3} maxLength={2000}
              className="w-full bg-black/40 border border-white/15 rounded px-3 py-2 text-sm text-white" />
            <button data-testid="bid-submit" onClick={submitBid} disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 py-2 bg-gold text-luxury-black text-xs font-body uppercase tracking-wider rounded hover:bg-gold/90 disabled:opacity-40">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Submit bid · 10-min lock
            </button>
          </div>
        )}
      </div>
      {datasetModalOpen && (
        <DatasetUploadModal
          jobId={job.id}
          existingDataset={dataset}
          onUploaded={async (ds) => {
            setDataset(ds);
            setDatasetModalOpen(false);
            if (pendingAnalystBidId) {
              const pid = pendingAnalystBidId;
              setPendingAnalystBidId(null);
              if (confirm("Dataset attached. Accept the ARIA-Analyst bid and start the work?")) {
                await performAccept(pid);
              }
            }
          }}
          onClose={() => { setDatasetModalOpen(false); setPendingAnalystBidId(null); }}
        />
      )}
    </div>
  );
}
